import type { Json } from "@/integrations/supabase/types";

// Error source types
export type ErrorSource = 'compiler' | 'auth' | 'llm' | 'function' | 'extraction';

// Error severity types
export type ErrorSeverity = 'error' | 'warning' | 'info';

// Unified error interface for all sources
export interface UnifiedError {
  id: string;
  source: ErrorSource;
  severity: ErrorSeverity;
  code: string;
  message: string;
  created_at: string;
  user_id: string | null;
  user_email: string | null;
  context: ErrorContext;
  raw_payload: Json;
}

// Context specific to different error sources
export interface ErrorContext {
  // Compiler/Extraction context
  reporting_run_id?: string;
  exam_family_id?: string;
  section_token?: string;
  structure_key?: string;
  finding_unit_id?: string;
  atom_id?: string;
  observation_id?: string;
  // LLM context
  provider?: string;        // 'gemini' | 'openai' | 'openrouter'
  model?: string;           // 'gemini-2.5-pro', etc.
  attempt?: number;         // retry attempt number
  // Edge Function context
  function_name?: string;   // 'compile-report', 'session-bootstrap', etc.
  duration_ms?: number;
  // Auth context
  session_id?: string;
  event_name?: string;
}

// Error summary for metrics
export interface ErrorSummary {
  total_24h: number;
  total_7d: number;
  error_rate_24h: number;
  error_rate_7d: number;
  most_common_code: string | null;
  most_common_count: number;
}

// Error distribution for charts
export interface ErrorDistribution {
  code: string;
  count: number;
  percentage: number;
}

// Date range filter options
export interface DateRangeFilter {
  startDate?: Date;
  endDate?: Date;
}

// Error filters
export interface ErrorFilters extends DateRangeFilter {
  severity?: ErrorSeverity;
  code?: string;
  userId?: string;
}

// Paginated result
export interface PaginatedResult<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

// Compiler validation error from payload JSON
export interface CompilerValidationError {
  code: string;
  message: string;
  section_token?: string;
  structure_key?: string;
  severity: 'error' | 'warning';
}

// Compiler payload structure
export interface CompilerPayload {
  validation?: {
    errors?: CompilerValidationError[];
    warnings?: CompilerValidationError[];
  };
  [key: string]: unknown;
}

// Events table payload for auth/llm/function errors
export interface EventsPayload {
  error_code?: string;
  message?: string;
  function_name?: string;
  provider?: string;
  model?: string;
  attempt?: number;
  duration_ms?: number;
  [key: string]: unknown;
}

// Tab configuration
export interface ErrorTabConfig {
  id: ErrorSource;
  label: string;
  description: string;
}

export const ERROR_TABS: ErrorTabConfig[] = [
  { id: 'compiler', label: 'Compiler', description: 'Report compilation errors and warnings' },
  { id: 'auth', label: 'Auth', description: 'Authentication and session errors' },
  { id: 'llm', label: 'LLM', description: 'LLM gateway and provider errors' },
  { id: 'function', label: 'Functions', description: 'Edge function execution errors' },
  { id: 'extraction', label: 'Extraction', description: 'Clinical entity extraction events' },
];
